import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { SereniApi } from "../api/sereniApi";
import { Product } from "../models/Product";
import { Supplier } from "../models/Supplier";
import { AccountingEntity } from "../models/AccountingEntity";

interface FuneralHomeContextProps {
  accountingEntities: AccountingEntity[];
  products: Product[];
  suppliers: Supplier[];
  isLoading: boolean;
  error: string | null;
  selectedAccountingEntity: AccountingEntity | null;
  setSelectedAccountingEntity: (entity: AccountingEntity) => void;
  setProducts: (products: Product[]) => void;
  setSuppliers: (suppliers: Supplier[]) => void;
}

const FuneralHomeContext = createContext<FuneralHomeContextProps | undefined>(
  undefined
);

export const FuneralHomeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth0();
  const [accountingEntities, setAccountingEntities] = useState<AccountingEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccountingEntity, setSelectedAccountingEntity] =
    useState<AccountingEntity | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const selectAccountingEntity = (entity: AccountingEntity) => {
    setSelectedAccountingEntity(entity);
    setProducts([]);
  };

  useEffect(() => {
    const fetchFuneralHomes = async () => {
      try {
        const groupedData = await SereniApi.fetchAllGroupedFuneralhomes();
        console.log("Grouped Funeral Homes:", groupedData);
        
        setAccountingEntities(groupedData);
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchFuneralHomes();
    }

    return () => {
      setAccountingEntities([]);
    };

  }, [user]);

  return (
    <FuneralHomeContext.Provider
      value={{
        accountingEntities,
        products,
        suppliers,
        isLoading,
        error,
        selectedAccountingEntity,
        setSelectedAccountingEntity: selectAccountingEntity,
        setProducts,
        setSuppliers,
      }}
    >
      {children}
    </FuneralHomeContext.Provider>
  );
};

export const useFuneralHome = () => {
  const context = useContext(FuneralHomeContext);
  if (!context) {
    throw new Error("useFuneralHome must be used within a FuneralHomeProvider");
  }
  return context;
};
